import { useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/Layout';
import { Link } from 'react-router-dom';
import { Calendar, Loader2, TrendingUp, Clock, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to handle relative image URLs
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http')) return imageUrl;
  return `${BACKEND_URL}${imageUrl}`;
};

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API}/articles`);
        setArticles(response.data);
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatShortDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const cleanExcerpt = (text) => {
    if (!text) return '';
    return text
      .replace(/SEO TITLE/gi, '')
      .replace(/META DESCRIPTION/gi, '')
      .replace(/##?\s*\d*\.?\s*/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const getReadTime = (content) => {
    if (!content) return '3 min read';
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  // Split articles for layout
  const featuredArticle = articles[0];
  const secondaryArticles = articles.slice(1, 3);
  const remainingArticles = articles.slice(3);

  return (
    <Layout>
      <Helmet>
        <title>The Breakdown - Expert NBA Player Prop Analysis | PrimeStatLab</title>
        <meta name="description" content="In-depth NBA player prop analysis, betting strategies, and expert insights. Daily picks with data-driven analysis to help inform your sports betting decisions." />
        <link rel="canonical" href="https://primestatlab.com/articles" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="The Breakdown - NBA Player Prop Analysis | PrimeStatLab" />
        <meta property="og:description" content="In-depth NBA player prop analysis, betting strategies, and expert insights. Daily picks with data-driven analysis." />
        <meta property="og:url" content="https://primestatlab.com/articles" />
        <meta property="og:site_name" content="PrimeStatLab" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <div className="container mx-auto px-6 md:px-12 py-12" data-testid="articles-page">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="text-primary" size={24} />
            <span className="font-mono text-xs uppercase tracking-widest text-primary">Analysis & Insights</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">THE BREAKDOWN</h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            In-depth player prop analysis, betting strategies, and expert insights to sharpen your edge.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
            <Loader2 className="animate-spin" size={40} />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20" data-testid="no-articles">
            <p className="text-xl text-muted-foreground">No articles published yet.</p>
          </div>
        ) : (
          <>
            {/* Featured Section - Hero Article + 2 Secondary */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
              {/* Featured Article - Takes 2 columns */}
              {featuredArticle && (
                <Link
                  to={`/article/${featuredArticle.slug}`}
                  className="lg:col-span-2 bg-card border border-border hover:border-primary transition-all duration-300 group relative overflow-hidden"
                  data-testid={`article-card-${featuredArticle.slug}`}
                >
                  <div className="absolute top-4 left-4 z-10">
                    <span className="bg-primary text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">
                      Latest
                    </span>
                  </div>
                  {featuredArticle.featured_image && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={getImageUrl(featuredArticle.featured_image)}
                        alt={featuredArticle.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6 md:p-8">
                    <div className="flex items-center gap-4 mb-4 font-mono text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(featuredArticle.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {getReadTime(featuredArticle.content)}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4 group-hover:text-primary transition-colors">
                      {featuredArticle.title}
                    </h2>
                    <p className="text-muted-foreground leading-relaxed line-clamp-3 mb-4">
                      {cleanExcerpt(featuredArticle.excerpt)}
                    </p>
                    <span className="inline-flex items-center gap-2 font-heading text-sm uppercase tracking-widest text-primary group-hover:gap-3 transition-all">
                      Read Full Analysis <ChevronRight size={16} />
                    </span>
                  </div>
                </Link>
              )}

              {/* Secondary Articles Stack */}
              <div className="flex flex-col gap-6">
                {secondaryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="bg-card border border-border hover:border-primary transition-all duration-300 group flex-1"
                    data-testid={`article-card-${article.slug}`}
                  >
                    {article.featured_image && (
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={getImageUrl(article.featured_image)}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2 font-mono text-xs text-muted-foreground">
                        <Calendar size={12} />
                        {formatShortDate(article.created_at)}
                      </div>
                      <h3 className="text-lg font-heading font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <span className="font-mono text-xs uppercase tracking-widest text-primary">
                        Read More →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Divider */}
            {remainingArticles.length > 0 && (
              <div className="flex items-center gap-4 mb-8">
                <div className="h-px bg-border flex-1"></div>
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">More Analysis</span>
                <div className="h-px bg-border flex-1"></div>
              </div>
            )}

            {/* Remaining Articles Grid */}
            {remainingArticles.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {remainingArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="bg-card border border-border hover:border-primary transition-all duration-300 group"
                    data-testid={`article-card-${article.slug}`}
                  >
                    {article.featured_image && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={getImageUrl(article.featured_image)}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-4 mb-3 font-mono text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {formatShortDate(article.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {getReadTime(article.content)}
                        </span>
                      </div>
                      <h3 className="text-xl font-heading font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed line-clamp-2 mb-4">
                        {cleanExcerpt(article.excerpt)}
                      </p>
                      <span className="font-heading text-xs uppercase tracking-widest text-primary group-hover:underline">
                        Read More →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Newsletter CTA */}
            <div className="mt-16 bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/30 p-8 md:p-12 text-center">
              <h3 className="text-2xl md:text-3xl font-heading font-bold mb-4">Never Miss An Analysis</h3>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Get our latest player prop breakdowns and betting insights delivered to your feed. Follow us on social.
              </p>
              <div className="flex justify-center gap-4">
                <a 
                  href="https://youtube.com/@primestatlab" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-primary text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-primary/90 transition-colors"
                >
                  Subscribe on YouTube
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
